/**
 * Sleep tracker — pure helpers. The day's main block is bedtime → wake (which
 * may cross midnight). XP is daily-capped adherence to a nightly target, so it's
 * farm-proof and rewards hitting your target. `sumSleepXP` is the single
 * definition crediting the Personal domain once + the Sleep area rank.
 *
 * The 24-hour clock reuses `clockGeometry` (noon-top, clockwise) for time→angle.
 */
import { hhmmToMinutes } from './clockGeometry';

export type CycleType = 'mono' | 'biphasic' | 'everyman' | 'uberman';

export interface SleepEntry {
  date: string; // local YYYY-MM-DD (the wake day)
  bedtime: string; // "HH:MM"
  wakeTime: string; // "HH:MM"
  quality: number | null; // 1..5
}

import { POINTS } from './levelCalculation';

export interface CycleDef {
  id: CycleType;
  label: string;
  desc: string;
  /** Suggested nightly target (minutes) when this cycle is picked. */
  targetMinutes: number;
  /** Canonical block schedule, drawn as faint reference arcs on the clock. */
  blocks: { start: string; end: string }[];
}

export const DEFAULT_TARGET_MINUTES = 480; // 8h
export const MIN_TARGET_MINUTES = 120;
export const MAX_TARGET_MINUTES = 720;
export const DAILY_SLEEP_XP = 50;

export const CYCLE_TYPES: CycleDef[] = [
  { id: 'mono', label: 'Monophasic', desc: 'One nightly block', targetMinutes: 480, blocks: [{ start: '23:00', end: '07:00' }] },
  { id: 'biphasic', label: 'Biphasic', desc: 'Night sleep + a siesta', targetMinutes: 420, blocks: [{ start: '23:30', end: '05:30' }, { start: '13:00', end: '13:30' }] },
  { id: 'everyman', label: 'Everyman', desc: 'Short core + 3 naps', targetMinutes: 240, blocks: [{ start: '01:00', end: '04:00' }, { start: '09:00', end: '09:20' }, { start: '14:00', end: '14:20' }, { start: '21:00', end: '21:20' }] },
  { id: 'uberman', label: 'Uberman', desc: 'Six 20-minute naps', targetMinutes: 120, blocks: [{ start: '00:00', end: '00:20' }, { start: '04:00', end: '04:20' }, { start: '08:00', end: '08:20' }, { start: '12:00', end: '12:20' }, { start: '16:00', end: '16:20' }, { start: '20:00', end: '20:20' }] },
];

export function getCycle(id: CycleType): CycleDef {
  return CYCLE_TYPES.find(c => c.id === id) ?? CYCLE_TYPES[0];
}

/** Minutes slept in the main block (handles crossing midnight). 0 if unset. */
export function sleepMinutes(bedtime: string, wakeTime: string): number {
  if (!bedtime || !wakeTime) return 0;
  const bed = hhmmToMinutes(bedtime);
  let wake = hhmmToMinutes(wakeTime);
  if (wake <= bed) wake += 1440;
  return wake - bed;
}

export function entryMinutes(e: SleepEntry | undefined | null): number {
  return e ? sleepMinutes(e.bedtime, e.wakeTime) : 0;
}

export function clampTargetMinutes(m: number): number {
  if (isNaN(m)) return DEFAULT_TARGET_MINUTES;
  return Math.min(MAX_TARGET_MINUTES, Math.max(MIN_TARGET_MINUTES, Math.round(m)));
}

/** "7h 30m" / "8h". */
export function formatDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

/** 0..100 of target. */
export function sleepProgress(minutes: number, targetMinutes: number): number {
  const t = Math.max(1, targetMinutes || DEFAULT_TARGET_MINUTES);
  return Math.min(100, Math.max(0, (minutes / t) * 100));
}

/** Economy XP for one night — proportional to target, capped at 100%. */
export function nightSleepXP(minutes: number, targetMinutes: number): number {
  if (minutes <= 0) return 0;
  const t = Math.max(1, targetMinutes || DEFAULT_TARGET_MINUTES);
  // Config-backed cap (console → XP economy); the const above is the default.
  return Math.round(Math.min(1, minutes / t) * (POINTS.TRACKER_SLEEP_DAILY ?? DAILY_SLEEP_XP));
}

/** Total Sleep economy XP across logged nights (single source of truth). */
export function sumSleepXP(entries: ReadonlyArray<SleepEntry>, targetMinutes: number): number {
  return entries.reduce((sum, e) => sum + nightSleepXP(entryMinutes(e), targetMinutes), 0);
}

/** A night "met" once it reaches the target. */
export function isNightMet(e: SleepEntry, targetMinutes: number): boolean {
  return entryMinutes(e) >= (targetMinutes || DEFAULT_TARGET_MINUTES);
}

/**
 * Consecutive nights meeting target, ending at `todayStr`. An unlogged/short
 * today doesn't break the streak (mirrors the other trackers).
 */
export function sleepStreak(
  entries: ReadonlyArray<SleepEntry>,
  todayStr: string,
  targetMinutes: number,
  shift: (ymd: string, n: number) => string
): number {
  const met = new Map<string, boolean>();
  for (const e of entries) met.set(e.date, isNightMet(e, targetMinutes));

  let streak = 0;
  let cursor = met.get(todayStr) ? todayStr : shift(todayStr, -1);
  while (met.get(cursor)) {
    streak++;
    cursor = shift(cursor, -1);
  }
  return streak;
}
